/**
 * <MeetYourLawyerCard> — the tenant-branded, pre-pay trust primitive
 * (accountable-attorney-primitive §5, P1-1). Renders the named, licensed,
 * personally-accountable attorney BEFORE Sign/Pay:
 *
 *   • displayName + bar# linked to the STATE's own bar lookup (server-composed,
 *     the client's own click — never a company "verified" badge),
 *   • a dated e-signature indicator (R4),
 *   • a "Talk to your attorney before you commit" phone-call CTA (CR2),
 *   • a redacted proof-of-work sample link (CR5),
 *   • the 24h human-response promise (CR9),
 *   • the fee split (legal fee attorney-direct + disclosed flat platform fee),
 *   • the covered-scope summary, and the accountability sentence (CR6).
 *
 * Purely presentational — it takes a normalized `MeetCardData` (built from an
 * in-portal engagement or the public /meet/:token response) and an optional
 * `requestCall` handler. It renders UNDER the tenant's brand (the surrounding
 * <TenantThemeProvider> supplies the palette via CSS vars) with the ATTORNEY's
 * own framing — NOT AVOWAL branding. Every trust field is optional; the card
 * degrades to name-only when the sibling API hasn't served the rest yet.
 */
import type { MeetCardData } from '@/lib/meet';
import { useTenant } from '@/lib/tenant';
import styles from './MeetYourLawyer.module.css';

export interface RequestCallState {
  /** Present ⇒ the CTA is an actionable button (authenticated portal side). */
  onClick?: () => void;
  status?: 'idle' | 'busy' | 'sent' | 'error';
  errorText?: string;
  /** Shown when there's no onClick (public page / endpoint unkeyed). */
  fallbackNote?: string;
}

function usd(cents: number | null | undefined, currency = 'USD'): string | null {
  if (cents === null || cents === undefined) return null;
  const n = Number(cents);
  if (!Number.isFinite(n)) return null;
  return (n / 100).toLocaleString(undefined, { style: 'currency', currency });
}

function fmtDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

function firstName(name: string): string {
  return name.trim().split(/\s+/)[0] || name;
}

export default function MeetYourLawyerCard({
  data,
  requestCall,
}: {
  data: MeetCardData;
  requestCall?: RequestCallState;
}) {
  const { displayName: tenantName } = useTenant();
  const a = data.attorney;
  const firm = a.firmName?.trim() || tenantName;
  const verifyUrl = a.verify?.profileUrl || a.verify?.lookupUrl || null;
  const stateName = a.verify?.stateName || a.barState || null;

  const fees = data.fees;
  const currency = fees?.currency || 'USD';
  const legal = usd(fees?.legalFeeCents, currency);
  const platform = usd(fees?.platformFeeCents, currency);

  const slaHours = data.sla?.hours ?? 24;
  const slaPromise = data.sla?.promise || `A human replies within ${slaHours} hours.`;

  const callAvailable = data.call?.available !== false;
  const callStatus = requestCall?.status ?? 'idle';

  return (
    <section className={styles.card} aria-label="Meet your lawyer">
      <div className={styles.kicker}>/ meet your lawyer</div>

      <header className={styles.head}>
        <h3 className={styles.name}>{a.displayName}</h3>
        {firm && <div className={styles.firm}>{firm}</div>}
        {a.tagline && <p className={styles.tagline}>{a.tagline}</p>}
      </header>

      {/* Bar number → the STATE's own lookup. The client verifies for themselves. */}
      {(a.barNumber || verifyUrl) && (
        <div className={styles.verifyRow}>
          <div className={styles.verifyMain}>
            {a.barNumber && (
              <span className={styles.bar}>
                Bar #{a.barNumber}{stateName ? ` · ${stateName}` : ''}
              </span>
            )}
            <span className={`${styles.pill} ${data.verified ? styles.pillOk : styles.pillPending}`}>
              {data.verified ? 'Bar verified' : 'Bar verification pending'}
            </span>
          </div>
          {verifyUrl && (
            <a className={styles.verifyLink} href={verifyUrl} target="_blank" rel="noopener noreferrer">
              Verify on {stateName ? `${stateName}'s` : 'the state'} official site ↗
            </a>
          )}
        </div>
      )}

      {data.accountability && <p className={styles.accountability}>{data.accountability}</p>}

      {/* Dated e-signature warrant. */}
      {data.eSignature && (
        <div className={styles.sigRow}>
          <span className={styles.sigLabel}>E-signature</span>
          <span className={styles.sigVal}>
            {data.eSignature.signedAt
              ? `Signed as ${data.eSignature.willSignAs || a.displayName} · ${fmtDate(data.eSignature.signedAt)}`
              : `Will sign as ${data.eSignature.willSignAs || a.displayName} — signature pending`}
          </span>
        </div>
      )}

      {/* Fee split — visible before Pay (CR3). Attorney-direct legal fee + a
          disclosed flat platform fee the client never pays here (D0-payer). */}
      {(legal !== null || platform !== null) && (
        <div className={styles.fees}>
          {legal !== null && (
            <div className={styles.feeRow}>
              <span className={styles.feeLabel}>Legal fee <em>— paid to {firm}</em></span>
              <span className={styles.feePay}>{legal}</span>
            </div>
          )}
          {platform !== null && (
            <div className={`${styles.feeRow} ${styles.feeInfo}`}>
              <span className={styles.feeLabel}>Platform software fee <em>— billed to your attorney, not collected here</em></span>
              <span className={styles.feeInfoVal}>{platform}</span>
            </div>
          )}
          <div className={styles.feeNote}>
            {fees?.note || 'You pay your attorney directly for the legal work. Nothing is filed in your name until you approve it.'}
          </div>
        </div>
      )}

      {/* Covered scope (CR4). */}
      {data.scope?.description && (
        <div className={styles.scope}>
          <div className={styles.scopeLabel}>What your attorney is taking on</div>
          <p className={styles.scopeDesc}>{data.scope.description}</p>
          {!!data.scope.deliverables?.length && (
            <ul className={styles.deliverables}>
              {data.scope.deliverables.map((d, i) => (
                <li key={i} className={styles.deliverable}>{d.description}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      <div className={styles.assurances}>
        {data.proofSample?.url && (
          <a className={styles.sampleLink} href={data.proofSample.url} target="_blank" rel="noopener noreferrer">
            See a redacted work sample →
          </a>
        )}
        <span className={styles.sla}>{slaPromise}</span>
      </div>

      {/* CR2 — talk to your attorney before you commit. */}
      {callAvailable && (
        <div className={styles.callBox}>
          {requestCall?.onClick ? (
            <>
              <button
                type="button"
                className={styles.callBtn}
                disabled={callStatus === 'busy' || callStatus === 'sent'}
                onClick={requestCall.onClick}
              >
                {callStatus === 'busy'
                  ? 'Requesting…'
                  : callStatus === 'sent'
                    ? 'Call requested ✓'
                    : `Talk to ${firstName(a.displayName)} before you commit`}
              </button>
              {callStatus === 'sent' && (
                <span className={styles.callNote}>
                  {firstName(a.displayName)} will reach out to you directly.
                </span>
              )}
              {callStatus === 'error' && (
                <span className={styles.callErr}>
                  {requestCall.errorText || 'Could not send that request.'}
                </span>
              )}
            </>
          ) : (
            <span className={styles.callNote}>
              {requestCall?.fallbackNote
                || `Want to talk first? ${firstName(a.displayName)} can call you before you commit — reply to your invitation to set it up.`}
            </span>
          )}
        </div>
      )}
    </section>
  );
}
